import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { App, Button, Form, Input, InputNumber, Table, Typography } from 'antd'
import { billMoneySchema } from '@super-mega/server/contracts/bill'
import { useTRPC } from '@/trpc/react.ts'
import { GroupBillPicker } from './group-bill-picker.tsx'
import { OrganizationAccountSelect } from './organization-account-select.tsx'
import { snapshotObject } from './utils/finance-snapshot.ts'

export function GroupDraftEditor({
  group: initialGroup,
  onClose,
  onSaved
}: {
  group: {
    id: number
    organizationId: number
    customerId: number
    collectionAccountId: number | null
    note: string | null
    updatedAt: Date
    items: {
      billId: number
      releasedAt: Date | null
      billSnapshot: unknown
      outstandingAmount: string
      billVersion: number
    }[]
  }
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [group] = useState(initialGroup)
  const [open, setOpen] = useState(true)
  const [amounts, setAmounts] = useState<Partial<Record<number, string | null>>>({})
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [rows, setRows] = useState(() => group.items.filter((item) => !item.releasedAt))
  const [picking, setPicking] = useState(false)
  const [ids, setIds] = useState(() => rows.map((item) => item.billId))
  const selectedIds = new Set(ids)
  const save = useMutation(trpc.bill.editGroup.mutationOptions())
  const invalidAmount = ids.some((id) => amounts[id] !== undefined && !billMoneySchema.safeParse(amounts[id]).success)
  return (
    <ModalForm<{ collectionAccountId?: number; note?: string }>
      open={open}
      onOpenChange={setOpen}
      title="调整组内账单 · 草稿"
      layout="vertical"
      initialValues={{ collectionAccountId: group.collectionAccountId ?? undefined, note: group.note }}
      modalProps={{ afterClose: onClose, destroyOnHidden: true, mask: { closable: false }, width: '56rem' }}
      submitter={{ submitButtonProps: { loading: save.isPending, disabled: !ids.length || invalidAmount } }}
      onFinish={async (v) => {
        try {
          await save.mutateAsync({
            groupId: group.id,
            expectedUpdatedAt: group.updatedAt,
            billIds: ids,
            amountChanges: rows
              .filter(
                (item) =>
                  selectedIds.has(item.billId) &&
                  amounts[item.billId] != null &&
                  amounts[item.billId] !== item.outstandingAmount
              )
              .map((item) => ({
                billId: item.billId,
                expectedVersion: item.billVersion,
                outstandingAmount: amounts[item.billId]!
              })),
            collectionAccountId: v.collectionAccountId ?? null,
            note: v.note ?? null
          })
          await onSaved()
          setOpen(false)
          return true
        } catch (e) {
          void message.error(e instanceof Error ? e.message : '保存失败')
          return false
        }
      }}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <Typography.Text>
          本组账单 · 已选择 {ids.length} / {rows.length} 张
        </Typography.Text>
        <Typography.Text type="secondary">移出后可重新加入，保存后生效</Typography.Text>
      </div>
      <Button className="mb-3" onClick={() => setPicking(true)}>
        添加账单
      </Button>
      {picking && (
        <GroupBillPicker
          group={group}
          onClose={() => setPicking(false)}
          onSelect={(bills) => {
            const added = bills
              .filter((b) => !rows.some((r) => r.billId === b.id))
              .map((b) => ({
                billId: b.id,
                billVersion: b.version,
                outstandingAmount: b.outstandingAmount,
                releasedAt: null,
                billSnapshot: { billNo: b.billNo, description: b.description }
              }))
            setRows([...rows, ...added])
            setIds([...new Set([...ids, ...bills.map((b) => b.id)])])
          }}
        />
      )}
      <Table
        rowKey="billId"
        size="small"
        className="mb-4"
        scroll={{ x: 'max-content' }}
        dataSource={rows}
        pagination={false}
        columns={[
          {
            title: 'ID',
            dataIndex: 'billId',
            render: (id: number) => <Typography.Text copyable>{id}</Typography.Text>
          },
          { title: '账单号', render: (_, row) => String(snapshotObject(row.billSnapshot).billNo ?? '—') },
          { title: '费用', render: (_, row) => String(snapshotObject(row.billSnapshot).description ?? '—') },
          {
            title: '待收金额',
            align: 'right',
            render: (_, row) => (
              <InputNumber<string>
                aria-label={`账单 ${row.billId} 待收金额`}
                className="w-36!"
                stringMode
                min="0"
                precision={2}
                controls={false}
                prefix="¥"
                disabled={!selectedIds.has(row.billId) || save.isPending}
                value={amounts[row.billId] === undefined ? row.outstandingAmount : amounts[row.billId]}
                status={amounts[row.billId] === null ? 'error' : undefined}
                onChange={(value) => setAmounts((previous) => ({ ...previous, [row.billId]: value }))}
                onPressEnter={(event) => event.preventDefault()}
              />
            )
          },
          {
            fixed: 'right',
            align: 'center',
            title: '操作',
            key: 'membership',
            render: (_, row) => (
              <Button
                type="link"
                disabled={save.isPending}
                onClick={() =>
                  setIds((previous) =>
                    previous.includes(row.billId)
                      ? previous.filter((id) => id !== row.billId)
                      : [...previous, row.billId]
                  )
                }
              >
                {ids.includes(row.billId) ? '移出本组' : '重新加入'}
              </Button>
            )
          }
        ]}
      />
      <Form.Item
        name="collectionAccountId"
        label="主体收款账户"
        tooltip="显示在收款单上，供客户付款使用；实际到账时仍需登记收款。"
      >
        <OrganizationAccountSelect purpose="bill.group.read" organizationId={group.organizationId} />
      </Form.Item>
      <Form.Item name="note" label="出单备注">
        <Input.TextArea maxLength={5000} />
      </Form.Item>
    </ModalForm>
  )
}
